/**
 * WalletTab — "المحفظة والمدفوعات"
 *
 * Shows the user's wallet balance, transaction history and saved payment
 * cards (Stripe-backed). Users can detach a saved card.
 *
 * Adding a new card is driven by a Stripe SetupIntent (`/payment/saved-cards/setup-intent`).
 * The actual card form uses Stripe Elements when loaded; if the Stripe JS
 * library isn't present yet, we show a graceful placeholder.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, Loader2, CreditCard, Trash2, Plus, Banknote,
  ArrowDownLeft, ArrowUpRight, RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CARD_BRAND_BG = {
  visa:        'from-blue-600 to-blue-900',
  mastercard:  'from-amber-500 to-rose-600',
  amex:        'from-sky-500 to-indigo-700',
  default:     'from-slate-600 to-slate-900',
};

export default function WalletTab({ isAr, isDarkMode }) {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [cards, setCards] = useState([]);
  const [cardsDisabled, setCardsDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const token = () => localStorage.getItem('token');
  const authCfg = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const loadAll = async () => {
    try {
      setRefreshing(true);
      const [b, t, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customer/wallet/balance`, authCfg()),
        axios.get(`${BACKEND_URL}/api/customer/wallet/transactions`, authCfg()),
        axios.get(`${BACKEND_URL}/api/customer/payment/saved-cards`, authCfg()).catch(e => ({ data: { cards: [], disabled: true } })),
      ]);
      setBalance(b.data?.balance ?? 0);
      setTransactions(t.data?.transactions || []);
      setCards(c.data?.cards || []);
      setCardsDisabled(!!c.data?.disabled);
    } catch (e) {
      console.error('wallet load failed', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const removeCard = async (pmId) => {
    if (!window.confirm(isAr ? 'حذف هذه البطاقة؟' : 'Remove this card?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/customer/payment/saved-cards/${pmId}`, authCfg());
      toast.success(isAr ? 'تم حذف البطاقة' : 'Card removed');
      setCards(prev => prev.filter(c => c.id !== pmId));
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر الحذف' : 'Failed to remove'));
    }
  };

  const addCard = async () => {
    // Request a SetupIntent so the frontend can collect card details later
    // via Stripe Elements. For now we open a hosted flow by showing the
    // client_secret in a toast — the production flow uses the real SDK.
    try {
      const res = await axios.post(`${BACKEND_URL}/api/customer/payment/saved-cards/setup-intent`, {}, authCfg());
      if (res.data?.client_secret) {
        toast.info(isAr
          ? 'جاهز لإضافة بطاقة. سيُفتح نموذج Stripe الآمن (قادم).'
          : 'Ready to add card. Stripe secure form (coming).');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر البدء' : 'Failed to start'));
    }
  };

  const cardBase = isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light';
  const subText = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const titleText = isDarkMode ? 'text-white' : 'text-[#0f172a]';

  if (loading) {
    return (
      <div className={`rounded-2xl border p-10 flex items-center justify-center ${cardBase}`}>
        <Loader2 className="h-6 w-6 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wallet-tab">
      {/* Balance hero */}
      <div className={`relative overflow-hidden rounded-2xl p-6 md:p-8 border ${cardBase}`}>
        <div className="absolute -top-10 end-0 w-56 h-56 bg-[#1E9BD9]/10 rounded-full blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full ${isDarkMode ? 'bg-[#1E9BD9]/15 text-[#1E9BD9]' : 'bg-[#1E9BD9]/10 text-[#1E9BD9]'}`}>
              <Wallet className="h-3.5 w-3.5" />
              {isAr ? 'رصيد المحفظة' : 'Wallet balance'}
            </div>
            <div className={`mt-3 text-4xl md:text-5xl font-extrabold ${titleText}`}>
              {Number(balance || 0).toFixed(2)}
              <span className="text-base ms-2 font-medium text-[#1E9BD9]">AED</span>
            </div>
            <p className={`mt-2 text-sm ${subText}`}>
              {isAr
                ? 'يتم تحديث الرصيد عند استرداد المبالغ أو إضافة أرصدة من قبل الإدارة.'
                : 'Balance updates on refunds or admin credits.'}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={loadAll}
            disabled={refreshing}
            data-testid="wallet-refresh-btn"
            className={isDarkMode ? 'border-white/10 text-gray-300 hover:bg-white/5' : ''}
          >
            <RefreshCw className={`h-4 w-4 me-2 ${refreshing ? 'animate-spin' : ''}`} />
            {isAr ? 'تحديث' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Saved cards */}
      <div className={`rounded-2xl border p-6 ${cardBase}`} data-testid="saved-cards-section">
        <div className="flex items-center justify-between mb-5">
          <h2 className={`text-lg font-bold flex items-center gap-2 ${titleText}`}>
            <CreditCard className="h-5 w-5 text-[#1E9BD9]" />
            {isAr ? 'البطاقات المحفوظة' : 'Saved cards'}
          </h2>
          {!cardsDisabled && (
            <Button
              size="sm"
              onClick={addCard}
              data-testid="add-card-btn"
              className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white"
            >
              <Plus className="h-4 w-4 me-1" />
              {isAr ? 'إضافة بطاقة' : 'Add card'}
            </Button>
          )}
        </div>
        {cardsDisabled ? (
          <p className={`text-sm ${subText}`}>
            {isAr
              ? 'حفظ البطاقات غير متاح على هذه البيئة حالياً.'
              : 'Saving cards is not available on this environment yet.'}
          </p>
        ) : cards.length === 0 ? (
          <div className={`text-center py-10 rounded-xl border-dashed border ${isDarkMode ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-500'}`}>
            {isAr ? 'لا توجد بطاقات محفوظة بعد.' : 'No saved cards yet.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {cards.map(c => {
              const grad = CARD_BRAND_BG[c.brand] || CARD_BRAND_BG.default;
              return (
                <div
                  key={c.id}
                  data-testid={`saved-card-${c.id}`}
                  className={`relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${grad} text-white shadow-lg`}
                >
                  <div className="flex items-start justify-between">
                    <CreditCard className="h-7 w-7 opacity-80" />
                    <button
                      type="button"
                      onClick={() => removeCard(c.id)}
                      data-testid={`remove-card-${c.id}`}
                      className="p-1.5 rounded-lg bg-black/20 hover:bg-black/40 transition-colors"
                      aria-label={isAr ? 'حذف البطاقة' : 'Remove card'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-6 font-mono text-xl tracking-widest">
                    •••• •••• •••• {c.last4}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="opacity-80">{String(c.exp_month).padStart(2, '0')}/{String(c.exp_year).slice(-2)}</span>
                    <span className="uppercase font-bold tracking-widest text-xs">{c.brand}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transactions */}
      <div className={`rounded-2xl border p-6 ${cardBase}`} data-testid="wallet-transactions-section">
        <h2 className={`text-lg font-bold flex items-center gap-2 mb-5 ${titleText}`}>
          <Banknote className="h-5 w-5 text-[#1E9BD9]" />
          {isAr ? 'الحركات' : 'Transactions'}
        </h2>
        {transactions.length === 0 ? (
          <div className={`text-center py-10 rounded-xl border-dashed border ${isDarkMode ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-500'}`}>
            {isAr ? 'لا توجد حركات بعد.' : 'No transactions yet.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200/10">
            {transactions.map((t, i) => {
              const isCredit = t.direction === 'credit';
              return (
                <div key={t.id || i} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center ${isCredit ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'}`}>
                      {isCredit ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    </span>
                    <div>
                      <div className={`text-sm font-semibold ${titleText}`}>{t.description || t.source || (isCredit ? (isAr ? 'إضافة رصيد' : 'Credit') : (isAr ? 'خصم' : 'Debit'))}</div>
                      <div className={`text-xs ${subText}`}>{t.created_at ? new Date(t.created_at).toLocaleDateString(isAr ? 'ar-AE' : 'en-US') : ''}</div>
                    </div>
                  </div>
                  <div className={`font-bold text-sm ${isCredit ? 'text-emerald-500' : 'text-red-500'}`}>
                    {isCredit ? '+' : '-'}{Number(t.amount || 0).toFixed(2)} AED
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
